import fuzzysort from 'fuzzysort';
import type {
  SearchResultItem,
  SpatialLayer,
  SpatialLayerObject,
  SpatialResult,
} from '../../GlobalMapSearch.types';
import { IGNORED_SPECIAL_CHARACTERS } from '../../GlobalMapSearch.utils';

/** Cap per-layer hits to keep the dropdown manageable. */
const MAX_RESULTS_PER_LAYER = 5;

/**
 * Minimum fuzzysort score to treat as a real match. Because we score per
 * leaf string (not per giant flattened blob), legitimate matches land in
 * roughly the -5 to -50 range, while chars-in-order coincidences score
 * much worse. Anything below -1000 is noise.
 */
const MIN_SCORE = -1000;

/**
 * Recursively walk an attributes value and push every meaningful leaf as a
 * standalone string into `out`, tagged with the dotted key path it came
 * from. We split per leaf (instead of joining the whole object into one
 * blob) so fuzzysort can score each field in isolation — otherwise a query
 * like "john" against a very long blob containing "manager John Smith" deep
 * inside scores in the hundreds of thousands negative and gets thrown out
 * by the threshold.
 *
 * Numbers and booleans are coerced to string so badge numbers / phone
 * numbers / `inactive: true` flags are still searchable. Array indices are
 * skipped from the path (`tags.0` -> `tags`) since they aren't meaningful
 * to the user.
 */
function collectLeafStrings(
  value: unknown,
  path: string,
  out: { text: string; field: string }[],
): void {
  if (value == null) return;
  if (typeof value === 'string') {
    if (value.trim()) out.push({ text: value, field: path });
    return;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    out.push({ text: String(value), field: path });
    return;
  }
  if (Array.isArray(value)) {
    for (const v of value) collectLeafStrings(v, path, out);
    return;
  }
  if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const next = path ? `${path}.${k}` : k;
      collectLeafStrings(v, next, out);
    }
  }
}

/** Pull a non-empty string off an unknown value (for top-level name/title). */
function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined;
}

interface SearchableLeaf {
  text: string;
  field: string;
  spatialObject: SpatialLayerObject;
  layerKey: string;
  layerTitle: string;
  priority: number;
}

interface RunSpatialAttributeSearchArgs {
  text: string;
  source: 'static' | 'dynamic';
  layers: SpatialLayer[];
  collected: SearchResultItem[];
  flush: () => void;
}

const DYNAMIC_PRIORITY_FIELD_PATTERNS = ['name', 'agency', 'unit'];

function includesAnyPattern(value: string, patterns: string[]): boolean {
  const lower = value.toLowerCase();
  return patterns.some(p => lower.includes(p));
}

/**
 * Generic in-memory fuzzy search over `spatial_objects.attributes`. Used by
 * both the static- and dynamic-layer executors. Each layer is searched
 * independently so the per-layer `MAX_RESULTS_PER_LAYER` cap is enforced
 * before pushing any hits into `collected`.
 *
 * Strategy: explode every attribute leaf string into its own searchable
 * record, score per-leaf with fuzzysort, then dedupe back to one result
 * per spatial object (keeping the best-scoring leaf as `matchedText`).
 *
 * The query is normalized the same way as Axon (`IGNORED_SPECIAL_CHARACTERS`)
 * so a user typing a hyphenated/spaced phone or ID still matches.
 */
export function runSpatialAttributeSearch({
  text,
  source,
  layers,
  collected,
  flush,
}: RunSpatialAttributeSearchArgs): void {
  const query = text.replace(IGNORED_SPECIAL_CHARACTERS, '');
  if (!query || layers.length === 0) return;

  let pushedAny = false;

  for (const layer of layers) {
    const objects = layer.spatial_objects;
    if (!objects?.length) continue;

    const layerTitle = layer.title || layer.name || layer.key;
    const layerSource = (layer.source || '').toLowerCase();
    const isIamRespondingLayer = layerSource.includes('iamresponding');

    const leaves: SearchableLeaf[] = [];
    for (const obj of objects) {
      const tagged: { text: string; field: string }[] = [];
      const topName = pickString((obj as Record<string, unknown>).name);
      const topTitle = pickString((obj as Record<string, unknown>).title);
      if (topName) tagged.push({ text: topName, field: 'name' });
      if (topTitle) tagged.push({ text: topTitle, field: 'title' });
      if (source === 'dynamic') {
        const dynamicObj = obj as Record<string, unknown>;
        const topAgency =
          pickString(dynamicObj.agency) || pickString(dynamicObj.agency_name);
        const topUnit =
          pickString(dynamicObj.unit) || pickString(dynamicObj.unit_id);
        if (topAgency) tagged.push({ text: topAgency, field: 'agency' });
        if (topUnit) tagged.push({ text: topUnit, field: 'unit' });
        if (isIamRespondingLayer) {
          const header = pickString(dynamicObj.header);
          if (header) tagged.push({ text: header, field: 'unit' });
        }
      }
      collectLeafStrings(obj.attributes, '', tagged);
      for (const t of tagged) {
        let priority = 0;
        if (
          source === 'dynamic' &&
          includesAnyPattern(t.field, DYNAMIC_PRIORITY_FIELD_PATTERNS)
        ) {
          priority += 10;
        }
        if (
          source === 'dynamic' &&
          isIamRespondingLayer &&
          (t.field === 'unit' || t.field.toLowerCase().includes('header'))
        ) {
          priority += 20;
        }
        leaves.push({
          text: t.text,
          field: t.field,
          spatialObject: obj,
          layerKey: layer.key,
          layerTitle,
          priority,
        });
      }
    }

    if (leaves.length === 0) continue;

    const results = fuzzysort.go(query, leaves, {
      keys: ['text'],
      threshold: MIN_SCORE,
      // Pull more than MAX so dedupe-by-object still has enough candidates
      // to fill the per-layer cap.
      limit: MAX_RESULTS_PER_LAYER * 10,
    });
    const rankedResults = [...results].sort((a, b) => {
      const byPriority = b.obj.priority - a.obj.priority;
      if (byPriority !== 0) return byPriority;
      return b.score - a.score;
    });

    // Dedupe: best leaf per spatial object wins. Map preserves insertion
    // order, and fuzzysort already returns results best-score-first, so
    // the first entry per object is the winner.
    const bestByObject = new Map<SpatialLayerObject, SearchableLeaf>();
    for (const r of rankedResults) {
      const leaf = r.obj;
      if (!bestByObject.has(leaf.spatialObject)) {
        bestByObject.set(leaf.spatialObject, leaf);
      }
    }

    const winners = Array.from(bestByObject.values());
    let pushedForLayer = 0;
    for (const leaf of winners) {
      if (pushedForLayer >= MAX_RESULTS_PER_LAYER) break;
      const result: SpatialResult = {
        source,
        layerKey: leaf.layerKey,
        layerTitle: leaf.layerTitle,
        spatialObject: leaf.spatialObject,
        matchedText: leaf.text,
        matchedField: leaf.field,
      };
      collected.push({ type: source, data: result });
      pushedForLayer += 1;
      pushedAny = true;
    }
  }

  if (pushedAny) flush();
}
